// 時間前 札のフォーミュラ価格の入力値と proto・表示の写像。 計算は backend (AC-BIN-005c)。
import { create } from '@bufbuild/protobuf'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { BiddingIntradayMarketAreaGroup } from '@ebalance/proto/bffuser/v1/bidding_intraday_market_model_pb'
import {
  BiddingIntradayFormulaOperator,
  BiddingIntradayFormulaPriceStatus,
  BiddingIntradayFormulaVariable,
  type BiddingIntradayOrderFormula,
  BiddingIntradayOrderFormulaSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { PreviewBiddingIntradayOrderFormulaPriceResponse } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import type { TFunction } from 'i18next'

import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import { isPlainDecimalInput } from '~/models/numberInputConstraint'
import type { FeeAdjustedPricePreviewQueryStatus } from '~/pages/IntradayBidding/models/feeAutoConsider'
import { areaGroupLabel } from '~/pages/IntradayBidding/models/marketAreaGroupLabel'

/** 変数の画面値 (design `#intraday-bnew-formula-base` の value)。 */
export const FORMULA_VARIABLES = [
  'spot',
  'id-last',
  'id-high',
  'id-low',
] as const
export type FormulaVariable = (typeof FORMULA_VARIABLES)[number]

/** 演算子の画面値 (design `#intraday-bnew-formula-op` の value)。 */
export const FORMULA_OPERATORS = ['+', '-', '*', '/'] as const
export type FormulaOperator = (typeof FORMULA_OPERATORS)[number]

/** 参照エリア。 システムプライスは変数がスポット約定の時だけ選べる。 */
export type FormulaReferenceArea =
  | { kind: 'area'; areaCode: AreaCode }
  | { kind: 'systemPrice' }

/**
 * 変数の値を求めた範囲 (backend のプレビュー応答の値。 AC-BIN-005): スポット約定は参照エリア、
 * 時間前の変数は参照エリアが属するエリアグループ。
 */
export type FormulaReferenceScope =
  | FormulaReferenceArea
  | { kind: 'areaGroup'; areaGroup: BiddingIntradayMarketAreaGroup }

export type IntradayFormulaInput = {
  variable: FormulaVariable
  referenceArea: FormulaReferenceArea
  operator: FormulaOperator
  /** オフセット値 (Decimal string)。 */
  offset: string
}

const VARIABLES: Record<
  FormulaVariable,
  {
    proto: BiddingIntradayFormulaVariable
    labelKey: string
    /** 時間前市場の約定から求める (開場前は値が無い。 AC-BIN-005a)。 */
    intraday: boolean
  }
> = {
  spot: {
    proto: BiddingIntradayFormulaVariable.SPOT_EXECUTION,
    labelKey: 'page:biddingIntraday.form.formulaVariableSpot',
    intraday: false,
  },
  'id-last': {
    proto: BiddingIntradayFormulaVariable.INTRADAY_LAST,
    labelKey: 'page:biddingIntraday.form.formulaVariableIdLast',
    intraday: true,
  },
  'id-high': {
    proto: BiddingIntradayFormulaVariable.INTRADAY_HIGH,
    labelKey: 'page:biddingIntraday.form.formulaVariableIdHigh',
    intraday: true,
  },
  'id-low': {
    proto: BiddingIntradayFormulaVariable.INTRADAY_LOW,
    labelKey: 'page:biddingIntraday.form.formulaVariableIdLow',
    intraday: true,
  },
}

const OPERATORS: Record<
  FormulaOperator,
  { proto: BiddingIntradayFormulaOperator; symbol: string }
> = {
  '+': { proto: BiddingIntradayFormulaOperator.ADD, symbol: '+' },
  '-': { proto: BiddingIntradayFormulaOperator.SUBTRACT, symbol: '-' },
  '*': { proto: BiddingIntradayFormulaOperator.MULTIPLY, symbol: '×' },
  '/': { proto: BiddingIntradayFormulaOperator.DIVIDE, symbol: '÷' },
}

/** オフセット値の入力欄の刻み (小数点以下 2 桁)。 */
export const FORMULA_OFFSET_STEP = 0.01

const FORMULA_OFFSET_INTEGER_DIGITS = 9
const FORMULA_OFFSET_DECIMAL_DIGITS = 2

/** オフセット値の受理範囲 (整数部 9 桁・小数点以下 2 桁。 backend と同じ) を超える桁を末尾から切り詰める。 */
export const constrainFormulaOffsetInput = (raw: string): string => {
  const m = /^([+-]?)(\d*)(\.?)(\d*)$/.exec(raw)
  if (m === null) return raw
  const [, sign, integer, dot, decimals] = m
  return `${sign}${integer.slice(0, FORMULA_OFFSET_INTEGER_DIGITS)}${dot}${decimals.slice(0, FORMULA_OFFSET_DECIMAL_DIGITS)}`
}

/** 参照エリアの選択肢でシステムプライスを表す値。 エリアは `String(AreaCode)`。 */
export const SYSTEM_PRICE_REFERENCE_VALUE = 'system'

export const isIntradayFormulaVariable = (v: FormulaVariable): boolean =>
  VARIABLES[v].intraday

/** オフセット値に単位 (円) が付く演算子か (× / ÷ は倍率なので付かない)。 */
export const isAdditiveFormulaOperator = (op: string): boolean =>
  op === '+' || op === '-'

export const formulaVariableLabel = (
  t: TFunction,
  v: FormulaVariable,
): string => t(VARIABLES[v].labelKey)

export const formulaOperatorSymbol = (op: FormulaOperator): string =>
  OPERATORS[op].symbol

export const formulaReferenceAreaLabel = (
  t: TFunction,
  ref: FormulaReferenceArea,
): string =>
  ref.kind === 'systemPrice'
    ? t('page:biddingIntraday.form.formulaReferenceSystemPrice')
    : convertAreaLabel(ref.areaCode)

/** 変数の値を求めた範囲の名前。 */
export const formulaPreviewScopeLabel = (
  t: TFunction,
  scope: FormulaReferenceScope,
): string =>
  scope.kind === 'areaGroup'
    ? areaGroupLabel(t, scope.areaGroup)
    : formulaReferenceAreaLabel(t, scope)

const referenceScopeOf = (
  response: PreviewBiddingIntradayOrderFormulaPriceResponse,
): FormulaReferenceScope | undefined => {
  const scope = response.referenceScope
  switch (scope.case) {
    case 'referenceScopeAreaCode':
      return { kind: 'area', areaCode: scope.value }
    case 'referenceScopeSystemPrice':
      return { kind: 'systemPrice' }
    case 'referenceScopeAreaGroup':
      return { kind: 'areaGroup', areaGroup: scope.value }
    default:
      return undefined
  }
}

const isFormulaVariable = (s: string): s is FormulaVariable =>
  (FORMULA_VARIABLES as readonly string[]).includes(s)

const isFormulaOperator = (s: string): s is FormulaOperator =>
  (FORMULA_OPERATORS as readonly string[]).includes(s)

export const parseFormulaReferenceArea = (
  s: string,
): FormulaReferenceArea | undefined => {
  if (s === SYSTEM_PRICE_REFERENCE_VALUE) return { kind: 'systemPrice' }
  const areaCode = JEPX_AREA_CODES.find((code) => String(code) === s)
  return areaCode === undefined ? undefined : { kind: 'area', areaCode }
}

/** フォームの生入力をフォーミュラにする。 欠けている・読めない値があれば undefined。 */
export const parseIntradayFormulaInput = (raw: {
  variable: string
  referenceArea: string
  operator: string
  offset: string
}): IntradayFormulaInput | undefined => {
  const referenceArea = parseFormulaReferenceArea(raw.referenceArea)
  if (
    !isFormulaVariable(raw.variable) ||
    !isFormulaOperator(raw.operator) ||
    referenceArea === undefined ||
    !isPlainDecimalInput(raw.offset)
  ) {
    return undefined
  }
  if (referenceArea.kind === 'systemPrice' && raw.variable !== 'spot') {
    return undefined
  }
  return {
    variable: raw.variable,
    referenceArea,
    operator: raw.operator,
    offset: raw.offset,
  }
}

/**
 * 参照エリア欄の値。 まだ選んでいない (空) 間は札のエリアに追従する (AC-BIN-005)。
 * 追従はこの関数だけが持ち、 欄の表示と送る式の両方がここを通る。
 */
export const resolveFormulaReferenceAreaValue = (
  chosen: string,
  orderArea: string,
): string => (chosen === '' ? orderArea : chosen)

/**
 * 変数を変えた後の、 選んだ参照エリア。 システムプライスはスポット約定の時だけ選べるので、
 * 時間前の変数にしたら選択を外して札のエリアへの追従に戻す。
 */
export const formulaReferenceAreaAfterVariableChange = (
  chosen: string,
  nextVariable: string | undefined,
): string =>
  nextVariable !== 'spot' && chosen === SYSTEM_PRICE_REFERENCE_VALUE
    ? ''
    : chosen

/** フォーミュラ欄の生入力。 `formulaReferenceArea` は選んだ値で、 空は札のエリアに追従する。 */
export type IntradayFormulaFormValues = {
  areaCode: string
  formulaVariable: string
  formulaReferenceArea: string
  formulaOperator: string
  formulaOffset: string
}

/** フォーミュラ欄そのものの生入力 (札のエリアを除く)。 */
export type IntradayFormulaFieldValues = Omit<
  IntradayFormulaFormValues,
  'areaCode'
>

/** フォーミュラ欄の初期値 (札作成と同じ: スポット約定・札のエリアに追従・+・オフセット未入力)。 */
export const EMPTY_FORMULA_FIELD_VALUES: IntradayFormulaFieldValues = {
  formulaVariable: 'spot',
  formulaReferenceArea: '',
  formulaOperator: '+',
  formulaOffset: '',
}

/** フォーム欄の生入力 (参照エリアは追従の規則を通す) をフォーミュラにする。 */
export const parseIntradayFormulaFormValues = (
  raw: IntradayFormulaFormValues,
): IntradayFormulaInput | undefined =>
  parseIntradayFormulaInput({
    variable: raw.formulaVariable,
    referenceArea: resolveFormulaReferenceAreaValue(
      raw.formulaReferenceArea,
      raw.areaCode,
    ),
    operator: raw.formulaOperator,
    offset: raw.formulaOffset,
  })

export const toBiddingIntradayOrderFormula = (
  input: IntradayFormulaInput,
): BiddingIntradayOrderFormula =>
  create(BiddingIntradayOrderFormulaSchema, {
    variable: VARIABLES[input.variable].proto,
    referenceArea:
      input.referenceArea.kind === 'systemPrice'
        ? { case: 'referenceSystemPrice', value: true }
        : { case: 'referenceAreaCode', value: input.referenceArea.areaCode },
    operator: OPERATORS[input.operator].proto,
    offset: { value: input.offset },
  })

/** 札に保存されたフォーミュラを画面の値に戻す。 未知の値 (deploy skew) は undefined。 */
export const fromBiddingIntradayOrderFormula = (
  formula: BiddingIntradayOrderFormula,
): IntradayFormulaInput | undefined => {
  const variable = FORMULA_VARIABLES.find(
    (v) => VARIABLES[v].proto === formula.variable,
  )
  const operator = FORMULA_OPERATORS.find(
    (op) => OPERATORS[op].proto === formula.operator,
  )
  const ref = formula.referenceArea
  const referenceArea: FormulaReferenceArea | undefined =
    ref.case === 'referenceSystemPrice'
      ? { kind: 'systemPrice' }
      : ref.case === 'referenceAreaCode'
        ? { kind: 'area', areaCode: ref.value }
        : undefined
  const offset = formula.offset?.value
  if (
    variable === undefined ||
    operator === undefined ||
    referenceArea === undefined ||
    offset === undefined
  ) {
    return undefined
  }
  return { variable, referenceArea, operator, offset }
}

/** 札に保存されたフォーミュラを欄の値に戻す (AC-BIN-005: 入札編集モーダルの初期値)。 読めない値は undefined。 */
export const toIntradayFormulaFieldValues = (
  formula: BiddingIntradayOrderFormula,
): IntradayFormulaFieldValues | undefined => {
  const input = fromBiddingIntradayOrderFormula(formula)
  if (input === undefined) return undefined
  return {
    formulaVariable: input.variable,
    formulaReferenceArea:
      input.referenceArea.kind === 'systemPrice'
        ? SYSTEM_PRICE_REFERENCE_VALUE
        : String(input.referenceArea.areaCode),
    formulaOperator: input.operator,
    formulaOffset: input.offset,
  }
}

/** フォーミュラの 1 行表示 (例: スポット約定 (東京) + 0.50)。 オフセット値は入力と同じ小数第 2 位までで出す。 */
export const formulaInputSummary = (
  t: TFunction,
  input: IntradayFormulaInput,
): string =>
  t('page:biddingIntraday.detail.formulaValue', {
    variable: formulaVariableLabel(t, input.variable),
    area: formulaReferenceAreaLabel(t, input.referenceArea),
    operator: formulaOperatorSymbol(input.operator),
    offset: Number(input.offset).toFixed(2),
  })

/**
 * 保存したフォーミュラの 1 行表示。 読み戻した値は末尾の 0 が落ちて届くので、
 * `formulaInputSummary` で入力と同じ桁にそろえる。
 */
export const formulaSummary = (
  t: TFunction,
  formula: BiddingIntradayOrderFormula,
): string => {
  const input = fromBiddingIntradayOrderFormula(formula)
  return input === undefined ? '—' : formulaInputSummary(t, input)
}

/** プレビューの計算結果の表示。 `pending` は入力不足・問い合わせ中、 `failed` は取得失敗・読めない応答 (どちらも design の「→ —」)。 */
export type FormulaPricePreview =
  | { kind: 'pending' }
  | { kind: 'failed' }
  | {
      kind: 'calculated'
      formulaPriceYenPerKwh: string
      referencePriceYenPerKwh?: string
      referenceScope?: FormulaReferenceScope
    }
  | { kind: 'variableUnavailable' }
  | {
      kind: 'notCalculable'
      referencePriceYenPerKwh?: string
      referenceScope?: FormulaReferenceScope
    }

/** 入札価格は計算できた時だけ載るので、 それ以外の手数料の補足は入力不足と同じに扱う (AC-BIN-005c)。 */
export const resolveFormulaPricePreview = (
  status: 'idle' | 'loading' | 'error' | 'success',
  response: PreviewBiddingIntradayOrderFormulaPriceResponse | undefined,
): {
  price: FormulaPricePreview
  feeStatus: FeeAdjustedPricePreviewQueryStatus
} => {
  if (status === 'idle' || status === 'loading') {
    return { price: { kind: 'pending' }, feeStatus: status }
  }
  if (status === 'error' || response === undefined) {
    return { price: { kind: 'failed' }, feeStatus: 'idle' }
  }
  const referencePriceYenPerKwh = response.referencePriceYenPerKwh?.value
  const referenceScope = referenceScopeOf(response)
  const formulaPriceYenPerKwh = response.formulaPriceYenPerKwh?.value
  switch (response.status) {
    case BiddingIntradayFormulaPriceStatus.CALCULATED:
      return formulaPriceYenPerKwh === undefined
        ? { price: { kind: 'failed' }, feeStatus: 'idle' }
        : {
            price: {
              kind: 'calculated',
              formulaPriceYenPerKwh,
              referencePriceYenPerKwh,
              referenceScope,
            },
            feeStatus: 'success',
          }
    case BiddingIntradayFormulaPriceStatus.VARIABLE_UNAVAILABLE:
      return { price: { kind: 'variableUnavailable' }, feeStatus: 'idle' }
    case BiddingIntradayFormulaPriceStatus.NOT_CALCULABLE:
      return {
        price: {
          kind: 'notCalculable',
          referencePriceYenPerKwh,
          referenceScope,
        },
        feeStatus: 'idle',
      }
    default:
      return { price: { kind: 'failed' }, feeStatus: 'idle' }
  }
}
